import { useFormik } from 'formik'
import React, { ChangeEvent, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'
import { TextInput } from '../../inputs/textInput'

import { InitiateRefundSchema } from '../../../validation/validate'

import { removeElementAtIndex } from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  OrderItemType,
  SingleOrder,
} from '../../../redux-toolkits/orders/orders.type'
import { useInitiateRefundMutation } from '../../../redux-toolkits/transactions/transactions.slice'
import { RefundType } from '../../../redux-toolkits/refunds/refunds.type'
import { ImageUploadButton } from '../../button/imageUploadButton'
import { ImageBanner } from '../../images/imageBanner'
import { ImageUploadPreviewModal } from './ImagePreviewModal'

export const InitiateRefundModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  items: string[]
  order?: SingleOrder
  type: RefundType
  orderItems?: OrderItemType[] | undefined
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  items = [],
  order,
  type = 'PARTIAL',
  orderItems,
}) => {
  const [uploadedImage, setUploadedImage] = useState<File>()

  const { handleModalOpen, modal, modalOpen, setModalOpen } = useModalControl()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    const selectedFile = event.target.files[0]
    setUploadedImage(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const [initiateRefund, { isLoading: initiatingRefund }] =
    useInitiateRefundMutation()

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    isValid,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      images: [],
      reason: '',
    } as {
      images: string[]
      reason: string
    },
    validationSchema: InitiateRefundSchema,
    onSubmit: async (values, { resetForm }) => {
      const resp = await initiateRefund({
        order: order?._id ?? '',
        affectedItems: items,
        type: type,
        refundAmount:
          orderItems
            ?.filter((item) => items.includes(item._id))
            .reduce((sum, item) => sum + (item.totalBasePrice ?? 0), 0) ?? 0,
        proof: values.images,
        reason: values.reason,
      })

      if (resp.data?.success) {
        resetForm()
        onSuccessCallback()
      }
    },
  })

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={'Initiate Refund'}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          {(values.images ?? []).length > 0 && (
            <div className="relative w-full col-span-full grid grid-cols-2 gap-2">
              {values.images?.map((url, idx) => {
                const key = `${idx}`

                return (
                  <ImageBanner
                    imageUrl={url}
                    loading={false}
                    className="
                            !w-full !max-h-[150px]
                            "
                    errors={''}
                    key={key}
                    onDelete={() => {
                      const imagesCopy = JSON.parse(
                        JSON.stringify(values.images),
                      )

                      setFieldValue(
                        `images`,
                        removeElementAtIndex(imagesCopy, idx),
                      )
                    }}
                  />
                )
              })}
            </div>
          )}

          <ImageUploadButton
            handleChange={(e) => {
              handleFileChange(e)
            }}
            showText
            icon={'pic-icon'}
            iconDim={52}
            text={`Upload Proof`}
            className="
            !relative !w-full
            !m-0 !top-0 !right-0
            !h-fit
            !rounded-lg
            !col-span-full
            "
          />
        </div>

        <TextInput
          type="text"
          name={`reason`}
          placeholder="Reason for refund"
          value={values.reason}
          onBlur={handleBlur}
          onChange={handleChange}
          errors={errors?.reason && touched?.reason ? errors?.reason : ''}
          hasIcon={false}
          className=""
        />

        <Button
          label="Proceed"
          type="button"
          className="mt-10"
          disabled={!isValid || initiatingRefund}
          loading={initiatingRefund}
          primary
          onClick={() => {
            handleSubmit()
          }}
        />

        {uploadedImage && (
          <ImageUploadPreviewModal
            image={uploadedImage}
            open={modal === 'imageUploadPreviewModal' && modalOpen}
            setOpen={setModalOpen}
            onCloseCallback={() => {
              setUploadedImage(undefined)
            }}
            onSuccessCallback={(url) => {
              setFieldValue(`images`, [...(values.images ?? []), url])
            }}
          />
        )}
      </div>
    </ModalComponent>
  )
}
